import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import { ArrowLeft, Phone, Mail, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { content, loading } = useSiteContent();
  const [service, setService] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (content && content.services) {
      const found = content.services.find(s => s.id === serviceId);
      setService(found);
    }
  }, [content, serviceId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-pulse text-[#2D4A5E] text-xl">Nalaganje...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gray-100 pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#2D4A5E] mb-4">Storitev ni najdena</h1>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#4CAF50] hover:underline mx-auto"
            >
              <ArrowLeft size={20} />
              Nazaj na domačo stran
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Get gallery images - use service gallery or main image
  const galleryImages = service.gallery_images?.length > 0 
    ? service.gallery_images 
    : [service.image];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2D4A5E]/70 to-[#2D4A5E]/90"></div>
        </div>
        <div className="relative z-10 text-center px-6 pt-20">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mx-auto mb-6"
          >
            <ArrowLeft size={20} />
            Nazaj
          </button>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
            {service.title}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {service.description}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Full Description */}
          {service.full_description && (
            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold text-[#2D4A5E] mb-4">O storitvi</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {service.full_description}
              </p>
            </div>
          )}

          {/* Gallery */}
          {galleryImages.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#2D4A5E] mb-6">Galerija</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.map((img, index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={img}
                      alt={`${service.title} - slika ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-[#2D4A5E] rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Vas zanima {service.title.toLowerCase()}?
            </h3>
            <p className="text-white/70 mb-6">
              Kontaktirajte nas za brezplačno ponudbo in posvetovanje.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${content.phone_numbers?.[0]?.number || content.phone}`}
                className="flex items-center justify-center gap-2 bg-[#4CAF50] text-white px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors"
              >
                <Phone size={20} />
                Pokličite nas
              </a>
              <button
                onClick={() => navigate('/#povprasevanje')}
                className="flex items-center justify-center gap-2 bg-white text-[#2D4A5E] px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Mail size={20} />
                Pošljite povpraševanje
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={32} />
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                className="absolute left-4 text-white hover:text-gray-300 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ChevronLeft size={48} />
              </button>
              <button
                className="absolute right-4 text-white hover:text-gray-300 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}

          <img
            src={galleryImages[currentImageIndex]}
            alt={`${service.title} - slika ${currentImageIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-4 left-0 right-0 text-center text-white">
            <p className="text-white/60">{currentImageIndex + 1} / {galleryImages.length}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetail;
